import { type AvailableDeleteURLs, type AvailableGetURLs, type AvailablePatchURLs, type AvailablePostURLs, type AvailablePutURLs, routes, type RouteTypes } from "./generated";
import type { AxiosInstance, AxiosRequestConfig } from "axios";

export type {RouteTypes};

/**
 * Replaces URL parameters in the format :param with corresponding values
 * @param url The URL with parameters to replace
 * @param params An object containing parameter values
 * @returns The URL with parameters replaced
 */
export function replaceUrlParams(url: string, params: Record<string, string | number>): string {
  return url.replace(/:(\w+)/g, (_, paramName) => {
    if (paramName in params) {
      const value = params[paramName];
      delete params[paramName];
      return String(value);
    }
    throw new Error(`Missing parameter: ${paramName}`);
  });
}

type RouteBodyValidation<T extends keyof typeof routes, M extends string> =
  (typeof routes)[T]['validation'] extends Record<M, infer V>
    ? V extends { body: infer B } ? B : never
    : never;

export function getRouteValidations<T extends keyof typeof routes, M extends keyof (typeof routes)[T]['validation'] & string>(url: T, method: M): RouteBodyValidation<T, M> {
  const validation = (routes[url].validation as Record<string, { body?: unknown }>)[method];
  if (!validation?.body) {
    throw new Error(`No validation found for ${url} ${method}`);
  }

  return validation.body as RouteBodyValidation<T, M>;
}

export const createApiClient = (axios: AxiosInstance) => ({
  async get<URIs extends AvailableGetURLs>(url: URIs, opts: { params?: RouteTypes[URIs]['get']['params'] & RouteTypes[URIs]['get']['querystring'] & Record<string, any> } & AxiosRequestConfig = {}) {
    const replacedUrl = replaceUrlParams(url, opts.params || {});
    return axios.get<RouteTypes[URIs]['get']['response']>(replacedUrl, opts);
  },

  async post<URIs extends AvailablePostURLs>(url: URIs, data: RouteTypes[URIs]['post']['body'], opts: { params?: RouteTypes[URIs]['post']['params'] & RouteTypes[URIs]['post']['querystring'] & Record<string, any> } & AxiosRequestConfig = {}) {
    const replacedUrl = replaceUrlParams(url, opts.params || {});
    return axios.post<RouteTypes[URIs]['post']['response']>(replacedUrl, data, opts);
  },

  async put<URIs extends AvailablePutURLs>(url: URIs, data: RouteTypes[URIs]['put']['body'], opts: { params?: RouteTypes[URIs]['put']['params'] & RouteTypes[URIs]['put']['querystring'] & Record<string, any> } & AxiosRequestConfig = {}) {
    const replacedUrl = replaceUrlParams(url, opts.params || {});
    return axios.put<RouteTypes[URIs]['put']['response']>(replacedUrl, data, opts);
  },

  async postForm<URIs extends AvailablePostURLs>(url: URIs, data: FormData, opts: { params?: RouteTypes[URIs]['post']['params'] & RouteTypes[URIs]['post']['querystring'] } & AxiosRequestConfig = {}) {
    const params = { ...opts.params };
    const replacedUrl = replaceUrlParams(url, params);
    return axios.post<RouteTypes[URIs]['post']['response']>(replacedUrl, data, { ...opts, params, headers: { ...opts.headers, 'Content-Type': 'multipart/form-data' } });
  },

  async putForm<URIs extends AvailablePutURLs>(url: URIs, data: FormData, opts: { params?: RouteTypes[URIs]['put']['params'] & RouteTypes[URIs]['put']['querystring'] } & AxiosRequestConfig = {}) {
    const params = { ...opts.params };
    const replacedUrl = replaceUrlParams(url, params);
    return axios.put<RouteTypes[URIs]['put']['response']>(replacedUrl, data, { ...opts, params, headers: { ...opts.headers, 'Content-Type': 'multipart/form-data' } });
  },

  async delete<URIs extends AvailableDeleteURLs>(url: URIs, opts: { params?: RouteTypes[URIs]['delete']['params'] & RouteTypes[URIs]['delete']['querystring'] & Record<string, any> } & AxiosRequestConfig = {}) {
    const replacedUrl = replaceUrlParams(url, opts.params || {});
    return axios.delete<RouteTypes[URIs]['delete']['response']>(replacedUrl, opts);
  },

  async patch<URIs extends AvailablePatchURLs>(url: URIs, data: RouteTypes[URIs]['patch']['body'], opts: { params?: RouteTypes[URIs]['patch']['params'] & RouteTypes[URIs]['patch']['querystring'] & Record<string, any> } & AxiosRequestConfig = {}) {
    const replacedUrl = replaceUrlParams(url, opts.params || {});
    return axios.patch<RouteTypes[URIs]['patch']['response']>(replacedUrl, data, opts);
  }
});
